import React from 'react'
import PropTypes from 'prop-types'
import { Area, AreaChart, ResponsiveContainer } from 'recharts'
import './TrendCard.scss'

/**
 * TrendCard - Metric card with sparkline chart
 *
 * @param {string} title - Metric name
 * @param {string|number} value - Current value
 * @param {Array} data - Time series data for sparkline [{value: number}]
 * @param {string} changePercent - Period over period change
 * @param {boolean} isPositive - Is change positive
 * @param {string} period - Comparison period (e.g., "vs last month")
 * @param {string} variant - Color variant
 * @param {boolean} loading - Show loading state
 */
const TrendCard = ({
	title,
	value,
	data = [],
	changePercent,
	isPositive = true,
	period,
	variant = 'primary',
	loading = false
}) => {
	const getChartColor = () => {
		const colors = {
			primary: '#3b82f6',
			success: '#10b981',
			warning: '#f59e0b',
			danger: '#ef4444',
			info: '#6366f1'
		}
		return colors[variant] || colors.primary
	}

	if (loading) {
		return (
			<div className={`trend-card trend-card--${variant}`}>
				<div className="trend-card__skeleton">
					<div className="skeleton skeleton--title"></div>
					<div className="skeleton skeleton--value"></div>
					<div className="skeleton skeleton--chart"></div>
				</div>
			</div>
		)
	}

	return (
		<div className={`trend-card trend-card--${variant}`}>
			<div className="trend-card__content">
				<h3 className="trend-card__title">{title}</h3>
				<p className="trend-card__value">{value}</p>
				{(changePercent || period) && (
					<p className="trend-card__change">
						{changePercent && (
							<span className={`trend-card__percent ${isPositive ? 'trend-card__percent--up' : 'trend-card__percent--down'}`}>
								{isPositive ? '↑' : '↓'} {changePercent}
							</span>
						)}
						{period && <span className="trend-card__period">{period}</span>}
					</p>
				)}
			</div>
			{data.length > 0 && (
				<div className="trend-card__chart">
					<ResponsiveContainer width="100%" height={60}>
						<AreaChart data={data} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
							<defs>
								<linearGradient id={`gradient-${variant}`} x1="0" y1="0" x2="0" y2="1">
									<stop offset="0%" stopColor={getChartColor()} stopOpacity={0.3} />
									<stop offset="100%" stopColor={getChartColor()} stopOpacity={0} />
								</linearGradient>
							</defs>
							<Area
								type="monotone"
								dataKey="value"
								stroke={getChartColor()}
								strokeWidth={2}
								fill={`url(#gradient-${variant})`}
								dot={false}
							/>
						</AreaChart>
					</ResponsiveContainer>
				</div>
			)}
		</div>
	)
}

TrendCard.propTypes = {
	title: PropTypes.string.isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	data: PropTypes.arrayOf(PropTypes.shape({
		value: PropTypes.number
	})),
	changePercent: PropTypes.string,
	isPositive: PropTypes.bool,
	period: PropTypes.string,
	variant: PropTypes.oneOf(['primary', 'success', 'warning', 'danger', 'info']),
	loading: PropTypes.bool
}

export default TrendCard
